import React, { useState, useEffect, useMemo } from "react";
import { Header1 } from "../comp/themes/modern/home/header/Header1";
import { ToastContainer, toast } from "react-toastify";

import { useSite } from "../../../store/useSite";
import { useAuth } from "../../../store/useAuth";
import { useFloorPlanner } from "../../../store/useFloorPlanner";

// Import custom nodes
import { BoothNode } from "../../floor-planner/nodes/BoothNode";
import { GateNode } from "../../floor-planner/nodes/GateNode";
import { TextNode } from "../../floor-planner/nodes/TextNode";
import { ImageNode } from "../../floor-planner/nodes/ImageNode";
import { BorderNode } from "../../floor-planner/nodes/BorderNode";
import { RectangleNode } from "../../floor-planner/nodes/RectangleNode";
import { PentagonNode } from "../../floor-planner/nodes/PentagonNode";
import { HexagonNode } from "../../floor-planner/nodes/HexagonNode";
import { CircleNode } from "../../floor-planner/nodes/CircleNode";
import { TriangleNode } from "../../floor-planner/nodes/TriangleNode";
import { StarNode } from "../../floor-planner/nodes/StarNode";
import { ArrowNode } from "../../floor-planner/nodes/ArrowNode";
import { LineNode } from "../../floor-planner/nodes/LineNode";

import { Check, Eclipse, X } from "lucide-react";
import RightSidePanel from "../../sidebar/RightSideBar";
import ApplyWizardModal from "./ApplyWizardModal";
import FullPagePreviewModal from "./FullPagePreviewModal";
import { getPublicExhibitorCategories } from "../../../services/exhibitor";
import { getPublicFloorMap } from "../../../services/floorPlan";
import {
  getExhibitorRequestForm,
  submitExhibitorRequest,
} from "../../../services/exhibitorRequest";
import { CATEGORY_COLORS } from "../../../utils/exhibitor";
import FloorPlanSection from "./FloorPlanSection";
import { useExhibitorStore } from "../../../store/useExhibitorStore";
import SizeShow from "../../booths/SizeShow";
import { useBase } from "../../../store/useBase";

const nodeTypes = {
  booth: BoothNode,
  gate: GateNode,
  text: TextNode,
  image: ImageNode,
  border: BorderNode,
  rectangle: RectangleNode,
  pentagon: PentagonNode,
  hexagon: HexagonNode,
  circle: CircleNode,
  triangle: TriangleNode,
  star: StarNode,
  arrow: ArrowNode,
  line: LineNode,
};

export default function SiteExhibitorCategory() {
  const { eventId } = useBase();
  const { eventData, siteUser } = useSite();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  console.log(eventData);

  // Floor Plan State
  const [floors, setFloors] = useState([]);
  const [selectedFloor, setSelectedFloor] = useState("floor1");
  const [nodes, setNodes] = useState([]);
  const [eventSettings, setEventSettings] = useState(null);
  const [isFullPagePreview, setIsFullPagePreview] = useState(false);
  const [boothCounts, setBoothCounts] = useState({
    available: 0,
    booked: 0,
    onHold: 0,
    total: 0,
  });
  const [allBooths, setAllBooths] = useState([]);

  // Exhibitor Store Actions
  const isApplying = useExhibitorStore((s) => s.isApplying);
  const setIsApplying = useExhibitorStore((s) => s.setIsApplying);
  const setFormFields = useExhibitorStore((s) => s.setFormFields);
  const setApplyFormData = useExhibitorStore((s) => s.setApplyFormData);
  const setSelectedCategory = useExhibitorStore((s) => s.setSelectedCategory);
  const setApplyStep = useExhibitorStore((s) => s.setApplyStep);
  const setSubmittingApply = useExhibitorStore((s) => s.setSubmittingApply);
  const resetApplyForm = useExhibitorStore((s) => s.resetApplyForm);

  // Booth Panel State (connected to store)
  const isBoothPanelOpen = useExhibitorStore((s) => s.isBoothPanelOpen);
  const setIsBoothPanelOpen = useExhibitorStore((s) => s.setIsBoothPanelOpen);
  const selectedBooth = useExhibitorStore((s) => s.selectedBooth);
  const setSelectedBooth = useExhibitorStore((s) => s.setSelectedBooth);

  const user = useMemo(
    () => (useAuth.getState().user || {}),
    [],
  );

  useEffect(() => {
    const fetchCategories = async () => {
      if (!eventId) return;
      setLoading(true);
      try {
        const r = await getPublicExhibitorCategories(eventId);
        const res = r.data;
        console.log("API Response", res);
        if (res.success || res.message === "success") {
          const apiCategories = res.data?.data || res.data || [];
          const safe = apiCategories.map((row, idx) => ({
            id: row.id ?? row.category_id,
            name: row.category_name ?? row.name ?? `New Category ${idx + 1}`,
            priceType: row.price_type || "free",
            price: row.price ?? 0,
            currency: row.currency || "₹",
            boothLength: row.booth_height ?? row.booth_length ?? 10,
            boothBreadth: row.booth_width ?? row.booth_breadth ?? 10,
            unit: row.booth_dimension_unit || row.unit || "ft",
            boothMembers:
              row.max_members ??
              row.booth_members ??
              row.benefits?.exhibitorPasses?.quantity ??
              2,
            features: row.features || [],
            color: row.category_color
              ? { bg: row.category_color }
              : CATEGORY_COLORS[idx % CATEGORY_COLORS.length],
          }));
          console.log("Mapped Categories", safe);

          setCategories(safe);
        }
      } catch (e) {
        console.error("Failed to load categories:", e);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, [eventId, user.token, user.user?.id]);

  useEffect(() => {
    const fetchFloors = async () => {
      if (!eventId) return;
      try {
        const response = await getPublicFloorMap(eventId);

        const res = response.data;
        console.log("Floor Plan Data", res.data);

        // Handle both res.data and direct res structure
        const rootData = res.data || res;

        if (rootData && rootData.floorPlans) {
          console.log("Public Floor Map Data Fetched:", rootData);

          const { floorPlans = [] } = rootData;

          const processedFloors = floorPlans.map((floor) => {
            let flowData = floor.react_flow_data || { nodes: [], edges: [] };

            if (typeof flowData === "string") {
              try {
                flowData = JSON.parse(flowData);
              } catch {
                flowData = { nodes: [], edges: [] };
              }
            }

            // Ensure nodes are standardized for the UI
            if (flowData.nodes && Array.isArray(flowData.nodes)) {
              flowData.nodes = flowData.nodes.map((node) => ({
                ...node,
                // Ensure booth types are consistent
                type:
                  node.type === "boothNode" || node.type === "booth"
                    ? "booth"
                    : node.type,
                data:
                  node.type === "boothNode" || node.type === "booth"
                    ? {
                        ...node.data,
                        company_logo: node.data?.company_logo || node.data?.logo,
                      }
                    : node.data,
              }));
            }

            return {
              id: floor.id.toString(),
              name: floor.floor_name,
              react_flow_data: flowData,
              focus_areas: floor.focus_areas || [],
            };
          });

          setFloors(processedFloors);

          // Handle event settings
          if (rootData.event_settings) {
            setEventSettings(rootData.event_settings);
            // Also sync with useFloorPlanner store so BoothNode can read it
            useFloorPlanner
              .getState()
              .setEventSettings(rootData.event_settings);
          }

          // Set Booth Counts from backend
          if (rootData.booth_counts) {
            setBoothCounts(rootData.booth_counts);
          }

          if (rootData.booths || rootData.all_booths) {
            setAllBooths(rootData.booths || rootData.all_booths);
          }

          if (
            processedFloors.length &&
            (selectedFloor === "floor1" || !selectedFloor)
          ) {
            setSelectedFloor(processedFloors[0].id);
          }
        } else {
          setFloors([]);
        }
      } catch (e) {
        console.error("Error fetching public floor map:", e);
        setFloors([]);
      }
    };
    fetchFloors();
  }, [eventId]);

  useEffect(() => {
    const fetchFormFields = async () => {
      if (!eventId) return;
      try {
        const response = await getExhibitorRequestForm(eventId, 1);
        const res = response.data;
        console.log("form field", res);

        const fieldsData = res.data?.fields || res.fields;

        if (fieldsData) {
          setFormFields(fieldsData);
          // Initialize form data with default values
          const initialData = {};
          fieldsData.forEach((field) => {
            initialData[field.id] = "";
          });
          setApplyFormData(initialData);
        }
      } catch (e) {
        console.error("Error fetching dynamic fields:", e);
      }
    };
    if (isApplying) fetchFormFields();
  }, [eventId, isApplying]);

  // Handle auto-apply from query params (e.g. from standalone floor plan)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("apply") === "true") {
      const boothNum = params.get("booth");
      if (boothNum) {
        setApplyFormData((prev) => ({
          ...prev,
          requested_booth_number: boothNum,
        }));
      }
      setIsApplying(true);
      setApplyStep(2);

      // Clear the params to avoid re-triggering on refresh
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [setIsApplying, setApplyStep, setApplyFormData]);

  // Load saved floor plan nodes for selected floor
  useEffect(() => {
    if (!selectedFloor || floors.length === 0) {
      setNodes([]);
      return;
    }

    const floor = floors.find((f) => f.id === selectedFloor);
    if (floor && floor.react_flow_data) {
      if (typeof floor.react_flow_data === "string") {
        try {
          const parsed = JSON.parse(floor.react_flow_data);
          setNodes(parsed.nodes || []);
        } catch (e) {
          setNodes([]);
        }
      } else {
        setNodes(floor.react_flow_data.nodes || []);
      }
    } else {
      setNodes([]);
    }
  }, [selectedFloor, floors]);

  const booths = nodes.filter((n) => n.type === "booth");
  const availableBooths = boothCounts.available;
  const bookedBooths = boothCounts.booked;
  const onHoldBooths = boothCounts.onHold;

  //nodes of floor plan
  const previewNodes = useMemo(() => {
    return nodes.map((node) => {
      const isHighlighted =
        isBoothPanelOpen &&
        selectedBooth &&
        String(selectedBooth.id) === String(node.id);
      const readOnlyNode = {
        ...node,
        draggable: false,
        selectable: node.type === "booth",
      };

      if (node.type === "booth") {
        let bgColor = "#FFFFFF"; // default available: white
        const status = node.data?.status || "available";

        if (status === "assigned") {
          bgColor = eventSettings?.booth_color_assigned || "#FF69B4"; // color mapping
        } else if (status === "reserved" || status === "onHold") {
          bgColor = eventSettings?.booth_color_reserved || "#FFB6C1"; // color mapping
        } else if (status === "available" || status === "inactive") {
          bgColor = eventSettings?.booth_color_available || "#FFFFFF"; // color mapping
        }

        if (isHighlighted) {
          bgColor = "#4f46e5"; // Keep highlight color as is for UX
        }

        readOnlyNode.data = {
          ...node.data,
          company_logo: node.data?.company_logo || node.data?.logo,
          backgroundColor: bgColor,
          locked: true,
          isHighlighted,
          blink: isHighlighted,
        };

        // Hydrate company details from live booths data if missing in node.data
        const boothId = node.data?.boothId;
        if (boothId && allBooths?.length > 0) {
          const invBooth = allBooths.find(
            (b) => String(b.id) === String(boothId),
          );
          if (invBooth && invBooth.exhibitor) {
            readOnlyNode.data = {
              ...readOnlyNode.data,
              company_name: invBooth.exhibitor.name || node.data.company_name,
              company_logo:
                invBooth.exhibitor.logo ||
                node.data.company_logo ||
                node.data.logo,
              status: invBooth.status || node.data.status || "assigned",
            };
          }
        }
      } else {
        readOnlyNode.data = { ...node.data, locked: true };
      }
      return readOnlyNode;
    });
  }, [nodes, selectedBooth, isBoothPanelOpen, eventSettings, allBooths]);

  // Benefit List Renderer
  const renderBenefit = (isEnabled, label, quantity = null) => {
    const isNumericCount =
      quantity !== null &&
      quantity !== undefined &&
      !["yes", "no"].includes(String(quantity).toLowerCase()) &&
      !isNaN(Number(quantity)) &&
      Number(quantity) > 0;

    return (
      <div className="flex items-center gap-3 py-2 text-sm text-gray-700 font-medium">
        <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded">
          {isEnabled ? (
            <div className="bg-green-500 rounded flex items-center justify-center p-0.5">
              <Check size={12} className="text-white" strokeWidth={4} />
            </div>
          ) : (
            <div className="bg-red-500 rounded flex items-center justify-center p-0.5">
              <X size={12} className="text-white" strokeWidth={4} />
            </div>
          )}
        </div>
        <span>
          {isNumericCount && isEnabled ? `${quantity} ` : ""}
          {label}
        </span>
      </div>
    );
  };

  const CategoryGridSkeleton = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className="bg-white rounded-lg shadow-xl overflow-hidden flex flex-col h-[460px] animate-pulse"
        >
          <div className="py-7 px-6 bg-slate-200/90 flex flex-col items-center justify-center space-y-2">
            <div className="h-5 w-32 rounded bg-slate-300/80" />
            <div className="h-8 w-24 rounded bg-slate-300/80" />
          </div>
          <div className="p-6 bg-white flex-1 flex flex-col justify-between">
            <div className="space-y-4">
              <div className="h-4 w-20 rounded bg-slate-200" />
              <div className="space-y-3 pt-1">
                {[1, 2, 3, 4, 5].map((b) => (
                  <div key={b} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded bg-slate-200 shrink-0" />
                    <div className={`h-3 rounded bg-slate-200 ${b % 2 === 0 ? "w-40" : "w-52"}`} />
                  </div>
                ))}
              </div>
            </div>
            <div className="pt-6">
              <div className="h-11 w-full rounded-lg bg-slate-200" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const handleApplyFromBooth = (booth) => {
    // Find matching category from state if possible
    let cat = categories.find(
      (c) => String(c.id) === String(booth.data?.category_id),
    );

    // If not found by ID, try finding by name (as Booth might only have category name)
    if (!cat && (booth.data?.category_name || booth.data?.exhibitorCategory)) {
      const catName = booth.data.category_name || booth.data.exhibitorCategory;
      cat = categories.find((c) => c.name === catName);

      // If still not found in state, create a fallback BUT warns the console
      // and only use it if it's potentially valid
      if (!cat) {
        console.warn(
          `Category "${catName}" not found in fetched categories. Falling back to booth data.`,
        );
        cat = {
          id: booth.data.category_id || catName, // This is where the issue was
          name: catName,
          benefits: booth.data.benefits,
          boothLength: booth.data.dimensions?.length || 10,
          boothBreadth: booth.data.dimensions?.breadth || 10,
          unit: "ft",
          price: booth.data.price,
          currency: "₹",
          boothMembers: booth.data.benefits?.exhibitorPasses?.quantity || 2,
        };
      }
    }

    if (cat) {
      setSelectedCategory(cat);
    }

    // Set form data with booth info
    setApplyFormData((prev) => ({
      ...prev,
      requested_booth_number: booth.data?.number || booth.data?.label,
    }));

    setSelectedBooth(booth);
    setIsBoothPanelOpen(false);
    setIsApplying(true);
    setApplyStep(2);
  };

  const handleApplySubmit = async () => {
    // Get latest state without causing component to re-render
    const storeState = useExhibitorStore.getState();
    const {
      selectedCategory,
      formFields,
      applyFormData,
      applyFormErrors,
      selectedBooth,
    } = storeState;

    if (!selectedCategory) {
      toast.error("Please select a category first.");
      return;
    }

    // Validation
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    formFields.forEach((f) => {
      const val = applyFormData[f.id];
      const isRequired = f.required || false;
      const fieldName = f.field_name || f.name;

      if (isRequired && !val) {
        newErrors[f.id] = `${f.label} is required`;
      } else if (
        val &&
        (f.type === "email" || fieldName === "email") &&
        !emailRegex.test(val)
      ) {
        newErrors[f.id] = "Please enter a valid email address";
      }
    });

    if (Object.keys(newErrors).length > 0) {
      useExhibitorStore.getState().setApplyFormErrors(newErrors);
      const firstError = Object.values(newErrors)[0];
      toast.error(firstError);
      return;
    }

    useExhibitorStore.getState().setApplyFormErrors({});

    setSubmittingApply(true);

    try {
      // Safety check: ensure requested_category_id is an integer
      const categoryId = parseInt(selectedCategory.id);
      if (isNaN(categoryId)) {
        toast.error(
          "Invalid category selected. Please select a valid category.",
        );
        setSubmittingApply(false);
        return;
      }

      const formData = new FormData();
      formData.append("event_id", eventId);
      formData.append("requested_category_id", categoryId);

      if (selectedBooth?.data?.id) {
        formData.append("requested_booth_id", selectedBooth.data.id);
      }

      const answers = [];
      let imageCounter = 0;

      formFields.forEach((field) => {
        const val = applyFormData[field.id];
        if (val === undefined || val === null || val === "") return;

        if (field.type === "file" || field.type === "image") {
          if (val instanceof File) {
            // Check if it's the system company_logo field or a custom one
            const isSystemLogo = field.name === "company_logo";
            const fileKey = isSystemLogo
              ? "company_logo"
              : `image_${imageCounter++}`;

            formData.append(fileKey, val);
            answers.push({ field_id: field.id, value: fileKey });

            if (field.is_system_field && field.name !== fileKey) {
              // Also map to top level for the initial record creation if name differs
              formData.append(field.name, val);
            }
          }
        } else {
          answers.push({ field_id: field.id, value: val });
          const normalizedName = (field.name || "").toLowerCase().trim();
          const isSystemField =
            field.is_system_field ||
            ["company_name", "email", "website_url", "website"].includes(
              normalizedName,
            );

          if (isSystemField) {
            // Map 'website' to 'website_url' for backend compatibility
            formData.append(
              normalizedName === "website" ? "website_url" : field.name,
              val,
            );
          }
        }
      });

      formData.append("answers", JSON.stringify(answers));

      console.log("--- formData entries ---");
      for (let [key, value] of formData.entries()) {
        console.log(key, value);
      }
      const r = await submitExhibitorRequest(formData, user.token);

      const res = r.data;
      if (res.message === "success" || res.status === 200 || res.success) {
        toast.success("Application submitted successfully!");
        useExhibitorStore.getState().setApplyFormErrors({});
        useExhibitorStore.getState().setIsSubmitted(true);
      } else {
        const errMsg = res.errorMessage || res.message || "Submission failed";
        useExhibitorStore.getState().setApplyFormErrors({ apiError: errMsg });
        throw new Error(errMsg);
      }
    } catch (e) {
      toast.error(
        e.response?.data?.errorMessage ||
          e.response?.data?.message ||
          e.message ||
          "Failed to submit application",
      );
    } finally {
      setSubmittingApply(false);
    }
  };

  const formatDateRange = (start, end) => {
    if (!start || !end) return "Dates TBA";
    const s = new Date(start);
    const e = new Date(end);
    const options = { month: "short", day: "numeric" };
    return `${s.toLocaleDateString("en-US", options)} to ${e.toLocaleDateString("en-US", { ...options, year: "numeric" })}`;
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col font-sans">
      <Header1 />
      {/* TOP SECTION: APPLY TO EXHIBIT */}
      <section
        className="text-center py-20 md:py-32 px-6"
        style={{
          background: "linear-gradient(135deg, #1e293b 0%, #334155 100%)",
          backgroundImage:
            "repeating-linear-gradient(45deg, rgba(255,255,255,0.05) 0px, rgba(255,255,255,0.05) 20px, transparent 20px, transparent 40px), linear-gradient(135deg, #1e293b 0%, #334155 100%)",
        }}
      >
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-black text-white mb-4 md:mb-6 tracking-tight uppercase px-4">
            APPLY TO EXHIBIT
          </h1>
          <p className="text-gray-300 text-xs md:text-base max-w-2xl mx-auto mb-8 md:mb-10 leading-relaxed px-6">
            Apply to exhibit with us. Our team will get in touch with you to
            discuss your goals and help you choose the best booth for your
            brand.
          </p>
          <button
            onClick={() => {
              resetApplyForm();
              setIsApplying(true);
            }}
            className="bg-white text-black px-8 md:px-10 py-3.5 md:py-4 rounded-full text-[10px] md:text-xs font-black uppercase tracking-widest hover:bg-gray-100 transition-all shadow-2xl active:scale-95"
          >
            Become an Exhibitor
          </button>
        </div>
      </section>
      {/* MIDDLE SECTION: FLOOR PLAN */}
      {floors.length > 0 && (
        <FloorPlanSection
          availableBooths={availableBooths}
          bookedBooths={bookedBooths}
          onHoldBooths={onHoldBooths}
          selectedFloor={selectedFloor}
          setSelectedFloor={setSelectedFloor}
          setIsFullPagePreview={setIsFullPagePreview}
          floors={floors}
          previewNodes={previewNodes}
          nodeTypes={nodeTypes}
          setSelectedBooth={setSelectedBooth}
          setIsBoothPanelOpen={setIsBoothPanelOpen}
          isBoothPanelOpen={isBoothPanelOpen}
          selectedBooth={selectedBooth}
          RightSidePanel={RightSidePanel}
          categories={categories}
          renderBenefit={renderBenefit}
          handleApplyFromBooth={handleApplyFromBooth}
          eventSettings={eventSettings}
        />
      )}

      {/* BOTTOM SECTION: EXHIBITOR CATEGORIES */}
      <section className="bg-[#1a365d] py-20 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12 text-center">
            <h2 className="text-3xl md:text-4xl font-black text-[#86efac] mb-3 tracking-wide uppercase">
              Exhibitor Categories
            </h2>
            <p className="text-white/70 text-sm md:text-base max-w-xl mx-auto">
              Choose the category that best fits your requirements and scale
              your impact
            </p>
          </div>

          {loading ? (
            <CategoryGridSkeleton />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {categories.map((cat, i) => {
                const isFree =
                  cat.priceType === "free" || Number(cat.price) === 0;
                const priceStr = isFree
                  ? "Free"
                  : `${cat.currency} ${Number(cat.price).toLocaleString()}`;

                return (
                  <div
                    key={cat.id}
                    className="bg-white rounded-lg shadow-xl overflow-hidden flex flex-col h-full transition-transform hover:scale-[1.01]"
                  >
                    <div
                      className="py-6 px-6 text-center text-white"
                      style={{
                        backgroundColor:
                          cat.color?.bg ||
                          CATEGORY_COLORS[i % CATEGORY_COLORS.length].bg,
                      }}
                    >
                      <h3 className="text-lg font-medium opacity-90 mb-1">
                        {cat.name}
                      </h3>
                      <div className="text-3xl font-bold">{priceStr}</div>
                    </div>

                    <div className="p-6 bg-white flex-1">
                      <h4 className="font-bold text-gray-900 mb-4 text-sm tracking-wide uppercase">
                        Benefits
                      </h4>
                      <div className="space-y-1">
                        {renderBenefit(
                          true,
                          <span className="inline-flex whitespace-pre-wrap">
                            Booth Dimensions:{" "}
                            <SizeShow
                              height={cat.boothLength}
                              width={cat.boothBreadth}
                              booth_dimension_unit={cat.unit}
                            />
                          </span>,
                        )}
                        {renderBenefit(true, "Booth Members", cat.boothMembers)}

                        {cat.features
                          ?.filter((f) => f.visible !== 0 && f.is_visible !== 0 && f.isVisible !== false)
                          .sort((a, b) => {
                            const isAEnabled =
                              a.enabled === 1 ||
                              a.enabled === true ||
                              a.is_enabled === 1 ||
                              String(a.value).toLowerCase() === "yes" ||
                              (a.value !== null && a.value !== undefined && !["no", "0"].includes(String(a.value).toLowerCase()));

                            const isBEnabled =
                              b.enabled === 1 ||
                              b.enabled === true ||
                              b.is_enabled === 1 ||
                              String(b.value).toLowerCase() === "yes" ||
                              (b.value !== null && b.value !== undefined && !["no", "0"].includes(String(b.value).toLowerCase()));

                            if (isAEnabled !== isBEnabled) {
                              return isAEnabled ? -1 : 1;
                            }
                            return (a.order || a.display_order || 0) - (b.order || b.display_order || 0);
                          })
                          .map((feature) => {
                            const isEnabled =
                              feature.enabled === 1 ||
                              feature.enabled === true ||
                              feature.is_enabled === 1 ||
                              String(feature.value).toLowerCase() === "yes" ||
                              (feature.value !== null && feature.value !== undefined && !["no", "0"].includes(String(feature.value).toLowerCase()));

                            const isNumericCount =
                              feature.value !== null &&
                              feature.value !== undefined &&
                              !["yes", "no"].includes(String(feature.value).toLowerCase()) &&
                              !isNaN(Number(feature.value)) &&
                              Number(feature.value) > 0;

                            const qty = isNumericCount ? feature.value : null;

                            return (
                              <div key={feature.id || feature.feature_id || feature.key}>
                                {renderBenefit(
                                  isEnabled,
                                  feature.name,
                                  qty,
                                )}
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="text-center">
            <button
              onClick={() => {
                resetApplyForm();
                setIsApplying(true);
              }}
              className="bg-black text-white px-8 py-3 rounded text-sm font-bold uppercase tracking-wider hover:bg-gray-900 transition-colors shadow-lg border border-gray-700"
            >
              Become an Exhibitor
            </button>
          </div>
        </div>
      </section>
      {/* APPLY WIZARD MODAL */}
      <ApplyWizardModal
        categories={categories}
        eventData={eventData}
        formatDateRange={formatDateRange}
        handleApplySubmit={handleApplySubmit}
        renderBenefit={renderBenefit}
      />

      {/* Floor plan preview modal */}
      <FullPagePreviewModal
        isOpen={isFullPagePreview}
        onClose={() => setIsFullPagePreview(false)}
        availableBooths={availableBooths}
        bookedBooths={bookedBooths}
        onHoldBooths={onHoldBooths}
        selectedFloor={selectedFloor}
        setSelectedFloor={setSelectedFloor}
        floors={floors}
        previewNodes={previewNodes}
        nodeTypes={nodeTypes}
        setSelectedBooth={setSelectedBooth}
        setIsBoothPanelOpen={setIsBoothPanelOpen}
        eventSettings={eventSettings}
      />
    </div>
  );
}
